import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Table } from 'antd';
import { useSelector } from 'react-redux';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import MaterialsColumnChanger from '../../materials/MaterialsColumnChanger';
import getMaterialColumns from '../../materials/materialTableColumns';
import useUpdateCostcodeIdMap from '../../costcodes/useUpdateCostcodeIdMap';

export default function ProjectMaterialsList({
  title = 'Materials',
  chosen,
  canEdit,
  onAddNew,
  onAddExisting,
  onDeleteSelected,
  onRowClick,
}) {
  const vendors = useSelector((state) => state.vendors.vendors);
  const costcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const [selectedColumns, setSelectedColumns] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useUpdateCostcodeIdMap({ items: chosen });

  const selectedColumnSet = useMemo(
    () => (selectedColumns ? new Set(selectedColumns) : null),
    [selectedColumns],
  );

  const columns = useMemo(() => getMaterialColumns({
    selectedColumns: selectedColumnSet,
    costcodeIdMap,
    vendorMap: vendors,
    isProject: true,
  }), [selectedColumnSet, costcodeIdMap, vendors]);

  useEffect(() => {
    const chosenIds = new Set(chosen.map((material) => material.id));
    setSelectedRowKeys((prev) => prev.filter((id) => chosenIds.has(id)));
  }, [chosen]);

  const rowSelection = useMemo(() => {
    if (!canEdit) return undefined;
    return {
      selectedRowKeys,
      onChange: setSelectedRowKeys,
      columnWidth: 48,
      getCheckboxProps: () => ({ className: 'ontraccr-checkbox' }),
    };
  }, [canEdit, selectedRowKeys]);

  const hasSelection = selectedRowKeys.length > 0;

  const onDeleteClick = useCallback(() => {
    const selectedRowKeysSet = new Set(selectedRowKeys);
    const selected = chosen.filter((material) => selectedRowKeysSet.has(material.id));
    onDeleteSelected(selected, () => setSelectedRowKeys([]));
  }, [chosen, selectedRowKeys, onDeleteSelected]);

  return (
    <div
      className="project-add-costcode-list-container"
      style={{
        position: 'absolute',
        inset: 0,
        top: 0,
      }}
    >
      <Row
        className="project-add-equipment-list-header"
        justify="space-between"
        align="middle"
      >
        <Col>
          <Row gutter={20} justify="start" align="middle">
            <Col className="project-add-costcode-header-title">
              {title}
            </Col>
            {hasSelection && (
              <Col style={{ color: '#4A6B8D', fontSize: 13 }}>
                Selected <b>{selectedRowKeys.length}</b>
              </Col>
            )}
          </Row>
        </Col>
        <Col>
          <Row justify="end" gutter={10} align="middle">
            {canEdit && hasSelection && (
              <Col>
                <OnTraccrButton
                  title="Delete"
                  type="back"
                  onClick={onDeleteClick}
                />
              </Col>
            )}
            {canEdit && !hasSelection && (
              <>
                <Col>
                  <OnTraccrButton
                    title="Add New"
                    onClick={onAddNew}
                  />
                </Col>
                <Col>
                  <OnTraccrButton
                    title="Add Existing"
                    onClick={onAddExisting}
                  />
                </Col>
              </>
            )}
            <Col>
              <MaterialsColumnChanger
                onChange={setSelectedColumns}
                isProject
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="project-add-equipment-table">
        <Table
          style={{ height: '100%' }}
          scroll={{
            y: `calc(100vh - ${canEdit ? 312 : 282}px)`,
            x: 'max-content',
          }}
          columns={columns}
          dataSource={chosen}
          pagination={false}
          size="small"
          rowKey="id"
          rowSelection={rowSelection}
          rowClassName="project-equipment-table-row"
          onHeaderRow={() => ({
            className: 'ontraccr-table-header',
          })}
          onRow={(record) => ({
            onClick: () => onRowClick?.(record),
          })}
        />
      </div>
    </div>
  );
}

ProjectMaterialsList.propTypes = {
  title: PropTypes.string,
  chosen: PropTypes.arrayOf(PropTypes.shape({})),
  canEdit: PropTypes.bool,
  onAddNew: PropTypes.func,
  onAddExisting: PropTypes.func,
  onDeleteSelected: PropTypes.func,
  onRowClick: PropTypes.func,
};

ProjectMaterialsList.defaultProps = {
  title: 'Materials',
  chosen: [],
  canEdit: false,
  onAddNew: undefined,
  onAddExisting: undefined,
  onDeleteSelected: undefined,
  onRowClick: undefined,
};
